import React, { useState } from 'react'
import style from './stars.module.scss'
import useSurveyLocale from '../../../modules/locale/useSurveyLocale'
import { IStarsProps } from './types'
import useInterfaceLocale from '../../../modules/locale/useInterfaceLocale'

const Stars = ({ label, value, size, na, index, onChange, setFocused }: IStarsProps) => {
  const { t } = useSurveyLocale()
  const [active, setActive] = useState<number>(value)
  const { localeOptions } = useInterfaceLocale()

  const onStarClick = (idx: number) => {
    setActive(idx + 1)
    if (onChange) {
      onChange(idx + 1)
    }
  }

  const onKeySelect = (event: React.KeyboardEvent) => {
    const { key } = event
    let newValue = null

    const isRight = 'ArrowRight' === key
    const isLeft = 'ArrowLeft' === key
    const isUp = 'ArrowUp' === key
    const isDown = 'ArrowDown' === key

    if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(key)) {
      event.preventDefault()
    }

    if (localeOptions.rtl) {
      if (isRight || isDown) {
        newValue = Math.max(active - 1, 1)
      } else if ((isLeft || isUp) && (na || active < size)) {
        newValue = null === active ? 1 : Math.min(active + 1, size + (na ? 1 : 0))
      }
    } else {
      if (isRight || isUp) {
        if (na || active < size) {
          newValue = null === active ? 1 : Math.min(active + 1, size + (na ? 1 : 0))
        }
      } else if (isLeft || isDown) {
        newValue = Math.max(active - 1, 1)
      }
    }
    if (newValue !== null) {
      onChange(newValue)
      setActive(newValue)
    }
  }

  return (
    <div
      tabIndex={0}
      className={`${style.Rating} ${localeOptions.rtl ? style.RatingRTL : ''}`}
      aria-label={`${label}: ${t<(n1: number, n2: number) => string>('questions', 'sr', 'current')(value, size)}`}
      data-testid="SW-Stars"
      aria-live="polite"
      onKeyDown={event => onKeySelect(event)}
      onFocus={() => setFocused?.(index)}
    >
      {Array.from({ length: na ? size + 1 : size }).map((_, idx) =>
        idx === size ? (
          <div
            key={idx}
            role="radio"
            aria-checked={value === idx + 1}
            aria-label={t('questions', 'sh', 'na')}
            className={`${style.RatingNa} ${idx + 1 <= active ? style.RatingNaActive : ''}`}
            onClick={() => onStarClick(idx)}
            onMouseEnter={() => setActive(size + 1)}
            onMouseLeave={() => setActive(value)}
          >
            <i className="icon-aytmfd-na" />
          </div>
        ) : (
          <div
            key={idx}
            role="radio"
            aria-checked={value === idx + 1}
            aria-label={t<(n1: number, n2: number) => string>('questions', 'sr', 'current')(idx + 1, size)}
            className={`${style.RatingStar} ${idx + 1 <= active && active <= size ? style.RatingStarActive : ''}`}
            onClick={() => onStarClick(idx)}
            onMouseEnter={() => setActive(idx + 1)}
            onMouseLeave={() => setActive(value)}
          >
            <i className="icon-aytmfd-star-rounded"></i>
          </div>
        )
      )}
    </div>
  )
}

export default Stars
